
import { motion } from 'framer-motion';

interface NodeData {
  id: string;
  color: string;
  path: string;
  endX: number;
  endY: number;
  label: string;
  description: string;
  position: 'left' | 'right' | 'top' | 'bottom';
  delay: number;
}

// Global layout constants for a 1000x600 SVG viewbox
const BASE_X = 2000; // Extended far right so it bleeds off the screen
const RADIUS = 24;

// Tightly packed spacing for the parallel pipes emerging from the right (16px apart)
const stemY = {
  ai: 268,        // Top outer
  realtime: 284,  // Top inner
  typesafe: 300,  // Center
  zeroconfig: 316, // Bottom inner
  allinone: 332,  // Bottom outer
};

const nodesData: NodeData[] = [
  {
    id: 'ai',
    color: '#ff9854', // Warm amber (matches --blue-bright)
    path: `M ${BASE_X} ${stemY.ai} L 1124 ${stemY.ai} A ${RADIUS} ${RADIUS} 0 0 1 1100 244 L 1100 80`,
    endX: 1100,
    endY: 80,
    label: 'Built-in AI',
    description: 'Schema generator, rule generator, data seeder.',
    position: 'top',
    delay: 0.0,
  },
  {
    id: 'allinone',
    color: '#8b3a1a', // Deep ember
    path: `M ${BASE_X} ${stemY.allinone} L 1004 ${stemY.allinone} A ${RADIUS} ${RADIUS} 0 0 0 980 356 L 980 520`,
    endX: 980,
    endY: 520,
    label: 'All-in-One Solution',
    description: 'Auth, storage, AI, and realtime.',
    position: 'bottom',
    delay: 0.0,
  },
  {
    id: 'realtime',
    color: '#e04812', // Burnt orange (matches --cyan-muted)
    path: `M ${BASE_X} ${stemY.realtime} L 884 ${stemY.realtime} A ${RADIUS} ${RADIUS} 0 0 1 860 260 L 860 200`,
    endX: 860,
    endY: 200,
    label: 'Realtime by Default',
    description: 'SSE + WebSocket. Subscribe to any collection.',
    position: 'top',
    delay: 0.2,
  },
  {
    id: 'zeroconfig',
    color: '#a63a1a', // Muted brick
    path: `M ${BASE_X} ${stemY.zeroconfig} L 764 ${stemY.zeroconfig} A ${RADIUS} ${RADIUS} 0 0 0 740 340 L 740 380`,
    endX: 740,
    endY: 380,
    label: 'Zero Configuration',
    description: 'Start with one command. No Docker, no YAML.',
    position: 'bottom',
    delay: 0.2,
  },
  {
    id: 'typesafe',
    color: '#ff5a1f', // Brand primary (matches --blue-core)
    path: `M ${BASE_X} ${stemY.typesafe} L 620 ${stemY.typesafe}`,
    endX: 620,
    endY: stemY.typesafe,
    label: 'Type-Safe API',
    description: 'Full TypeScript end-to-end. Zod validation.',
    position: 'left',
    delay: 0.4,
  },
];

const CustomIcons = {
  typesafe: ({ color }: { color: string }) => (
    <g>
      {/* Type-Safe API Icon — TypeScript badge with angle brackets */}
      <rect x="-11" y="-11" width="22" height="22" rx="3" fill="none" stroke={color} strokeWidth="1.5" />
      <text x="0" y="5" fontSize="13" fontWeight="600" textAnchor="middle" fill={color} className="font-sans">TS</text>
    </g>
  ),
  zeroconfig: ({ color }: { color: string }) => (
    <g>
      {/* Zero Configuration Icon — Terminal with play arrow */}

      {/* Terminal window */}
      <rect x="-13" y="-10" width="26" height="20" rx="3" fill="none" stroke={color} strokeWidth="1.5" />
      {/* Title bar dots */}
      <circle cx="-9" cy="-6" r="1.2" fill={color} />
      <circle cx="-5" cy="-6" r="1.2" fill={color} />
      {/* Prompt cursor line */}
      <path d="M -8 -1 L -4 2 L -8 5" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Blinking cursor */}
      <line x1="0" y1="5" x2="6" y2="5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </g>
  ),
  allinone: ({ color }: { color: string }) => (
    <g>
      {/* All-in-One Solution Icon — Unified cube / package */}

      {/* Top face */}
      <path d="M 0 -12 L 12 -5 L 0 2 L -12 -5 Z" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Right face */}
      <path d="M 12 -5 L 12 5 L 0 12 L 0 2 Z" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Left face */}
      <path d="M -12 -5 L 0 2 L 0 12 L -12 5 Z" fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Center vertical line for depth */}
      <line x1="0" y1="2" x2="0" y2="12" stroke={color} strokeWidth="1.5" />
    </g>
  ),
  realtime: ({ color }: { color: string }) => (
    <g>
      {/* Realtime by Default Icon — Broadcast / signal tower */}

      {/* Center antenna */}
      <line x1="0" y1="2" x2="0" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      {/* Antenna base */}
      <line x1="-5" y1="12" x2="5" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      {/* Antenna tip dot */}
      <circle cx="0" cy="0" r="2.5" fill={color} />

      {/* Inner signal arcs */}
      <path d="M -5 -4 A 6 6 0 0 1 5 -4" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      {/* Outer signal arcs */}
      <path d="M -9 -7 A 10 10 0 0 1 9 -7" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      {/* Outermost signal arcs */}
      <path d="M -13 -10 A 14.5 14.5 0 0 1 13 -10" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </g>
  ),
  ai: ({ color }: { color: string }) => (
    <g>
      {/* Built-in AI Icon (Sparkle) */}

      {/* Primary 4-pointed sparkle star */}
      <g transform="translate(-2, -1)">
        <path
          d="M 0 -12 C 0 -5, 3 -3, 10 0 C 3 3, 0 5, 0 12 C 0 5, -3 3, -10 0 C -3 -3, 0 -5, 0 -12 Z"
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </g>

      {/* Small accent sparkle — top right */}
      <g transform="translate(10, -8)">
        <path
          d="M 0 -4.5 C 0 -2, 1 -1, 3.5 0 C 1 1, 0 2, 0 4.5 C 0 2, -1 1, -3.5 0 C -1 -1, 0 -2, 0 -4.5 Z"
          fill={color}
        />
      </g>

      {/* Tiny accent sparkle — bottom right */}
      <g transform="translate(8, 6)">
        <path
          d="M 0 -2.5 C 0 -1, 0.6 -0.5, 2 0 C 0.6 0.5, 0 1, 0 2.5 C 0 1, -0.6 0.5, -2 0 C -0.6 -0.5, 0 -1, 0 -2.5 Z"
          fill={color}
        />
      </g>
    </g>
  )
};

const AnimatedPipe = ({ node }: { node: NodeData }) => {
  const duration = 1.2;
  return (
    <g>
      {/* Subtle glow layer behind the pipe */}
      <motion.path
        d={node.path}
        fill="none"
        stroke={node.color}
        strokeWidth="8"
        strokeOpacity="0.08"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration, delay: node.delay, ease: "easeInOut" }}
      />
      {/* Main pipe — thinner and with reduced opacity */}
      <motion.path
        d={node.path}
        fill="none"
        stroke={node.color}
        strokeWidth="2.5"
        strokeOpacity="0.7"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration, delay: node.delay, ease: "easeInOut" }}
      />
    </g>
  );
};

const FeatureNode = ({ node }: { node: NodeData }) => {
  const IconComponent = CustomIcons[node.id as keyof typeof CustomIcons];
  
  let textX = node.endX;
  let textY = node.endY;
  let textAnchor = "middle";

  if (node.position === 'left') {
    textX = node.endX - 55; // Pushes text perfectly outside the circle
    textAnchor = "end";
  } else if (node.position === 'right') {
    textX = node.endX + 55;
    textAnchor = "start";
  } else if (node.position === 'top') {
    textY = node.endY - 65;
    textAnchor = "end";
    textX = node.endX + 28; // Align right edge of text with the right edge of the node
  } else if (node.position === 'bottom') {
    textY = node.endY + 70; // Increased padding to match 'top' nodes
    textAnchor = "end";
    textX = node.endX + 28;
  }

  const appearanceDelay = node.delay + 1.0; 

  return (
    <g>
      {/* Animated Label Text */}
      <motion.text
        x={textX}
        y={textY}
        textAnchor={textAnchor as "middle" | "start" | "end" | "inherit"}
        dominantBaseline={node.position !== 'top' ? 'auto' : 'auto'}
        className="pointer-events-none select-none font-sans"
        initial={{ opacity: 0, y: node.position === 'top' ? 10 : 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: appearanceDelay + 0.2, duration: 0.5 }}
      >
        <tspan 
          x={textX} 
          dy="-0.3em" 
          fill="var(--text-primary)" 
          fontSize="22" 
          fontWeight="600" 
          className="tracking-tight"
        >
          {node.label}
        </tspan>
        <tspan 
          x={textX} 
          dy="1.6em" 
          fill="var(--text-secondary)" 
          fontSize="14" 
          fontWeight="400"
        >
          {node.description}
        </tspan>
      </motion.text>

      {/* 
        CRITICAL FIX: Decoupled <g> handles absolute placement, so the 
        <motion.g> scale transform operates strictly inside its own local center point.
      */}
      <g transform={`translate(${node.endX}, ${node.endY})`}>
        <motion.g
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 220, damping: 18, delay: appearanceDelay }}
        >
          {/* Subtle outer glow */}
          <circle cx="0" cy="0" r="32" fill={node.color} fillOpacity="0.06" />
          {/* Node base circle — thinner stroke with slight transparency */}
          <circle cx="0" cy="0" r="28" fill="var(--bg-surface)" stroke={node.color} strokeWidth="1.5" strokeOpacity="0.8" />
          <IconComponent color={node.color} />
        </motion.g>
      </g>
    </g>
  );
};

export default function ArchitectureDiagram() {
  return (
    <div className="flex items-center justify-center w-full p-4 h-full">
      <div className="relative w-full max-w-5xl aspect-[16/10]">
        <svg 
          viewBox="0 0 1000 600" 
          className="w-full h-full overflow-visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background Pipes layer */}
          {nodesData.map((node) => (
            <AnimatedPipe key={`pipe-${node.id}`} node={node} />
          ))}

          {/* Foreground Nodes layer */}
          {nodesData.map((node) => (
            <FeatureNode key={`node-${node.id}`} node={node} />
          ))}
        </svg>
      </div>
    </div>
  );
}

